import React, { useEffect, useRef, useState } from 'react';
import Banner from '../../components/Banner';
import Breadcrumb from '../../components/Breadcrumb';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO';
import { FaBolt, FaUsers, FaHandshake, FaChartLine, FaLightbulb, FaCogs, FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import '../../styles/pages/AboutUs.css';
import AboutImg from '../../images/about.jpg';
import GlobalPresence from '../../images/global-presence.jpg'
// import AboutUsBanner from '../../../public/images/about-banner.jpg'
import skilledProfessionals from "../../images/why-choose/SKILLED PROFESSIONALS.jpg";
import efficientHiring from "../../images/why-choose/EFFICIENT HIRING.jpg";
import adaptability from "../../images/why-choose/ADAPTABILITY.jpg";
import commitmentSuccess from "../../images/why-choose/COMMITMENT TO SUCCESS.jpg";
import industryExpertise from "../../images/why-choose/INDUSTRY EXPERTISE.jpg";
import provenResults from "../../images/why-choose/PROVEN RESULTS.jpg";
/* ================= ANIMATION VARIANTS ================= */
import { FaFlagUsa, FaFlag, FaMapMarkerAlt } from "react-icons/fa";
import { MdPublic } from "react-icons/md";
import { FaCheckCircle } from "react-icons/fa";
import { FaUserCheck } from "react-icons/fa";

const countries = [
    {
        name: "United States",
        icon: <FaFlagUsa />,
        region: "North America",
        desc: "Trusted by top tech firms for high-performance staffing and project delivery."
    },
    {
        name: "Canada",
        icon: <MdPublic />,
        region: "North America",
        desc: "Empowering businesses with agile recruitment and specialized training programs."
    },
    {
        name: "United Kingdom",
        icon: <FaFlag />,
        region: "Europe",
        desc: "Connecting global talent with industry leaders across various sectors."
    },
    {
        name: "India",
        icon: <FaMapMarkerAlt />,
        region: "Asia",
        desc: "Our hub for world-class talent, training, and operational excellence."
    },
];

const fadeUp = {
    hidden: { opacity: 0, y: 50 },
    show: {
        opacity: 1, y: 0,
        transition: { duration: 0.7, ease: "easeOut" }
    }
};

const staggerContainer = {
    hidden: {},
    show: { transition: { staggerChildren: 0.15 } }
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.85 },
    show: {
        opacity: 1, scale: 1,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

/* ================= DATA ================= */

const features = [
    {
        icon: <FaBolt />,
        title: "EFFICIENCY",
        desc: "We streamline the hiring process, ensuring quick and seamless connections with the right talent."
    },
    {
        icon: <FaUsers />,
        title: "SKILLED TALENT",
        desc: "Our extensive network includes top-tier IT professionals with the expertise to drive your projects forward."
    },
    {
        icon: <FaHandshake />,
        title: "PERSONALIZED STAFFING",
        desc: "We provide tailored solutions that align with your specific business objectives, ensuring a perfect match."
    }
];

const stats = [
    { value: 100, suffix: "+", label: "IT PROFESSIONALS PLACED" },
    { value: 50, suffix: "+", label: "BUSINESSES SERVED" },
    { value: 10, suffix: "+", label: "YEARS EXPERIENCE" },
    { value: 95, suffix: "%", label: "CLIENT MATCH RATE" }
];

const whyChooseUs = [
    {
        image: industryExpertise,
        title: "GLOBAL STAFFING EXPERTISE",
        desc: "We bring specialized knowledge in international staffing, connecting global talent with local business needs."
    },
    {
        image: provenResults,
        title: "STRONG CLIENT NETWORK",
        desc: "Trusted by a vast network of clients across the USA, Canada, UK, and India for reliable talent delivery."
    },
    {
        image: adaptability,
        title: "END-TO-END RECRUITMENT",
        desc: "Comprehensive recruitment solutions covering sourcing, screening, and seamless onboarding."
    },
    {
        image: skilledProfessionals,
        title: "INDUSTRY-EXPERIENCED TEAM",
        desc: "Our team consists of seasoned professionals with deep insights into the evolving IT landscape."
    },
    {
        image: efficientHiring,
        title: "QUICK TURNAROUND & QUALITY",
        desc: "Ensuring speed and quality in every placement, helping your business scale without delays."
    }
];

const highlights = [
    "Successfully supported multiple clients across the United States and international markets",
    "Delivered qualified IT professionals in Software Development, Cloud, DevOps, and Data Engineering",
    "Expertise in end-to-end recruitment lifecycle (sourcing, screening, onboarding)",
    "Strong network of pre-screened candidates ready for immediate deployment",
    "Experience in handling contract, full-time, and remote staffing models",
    "Assisted businesses in scaling teams quickly and efficiently",
    "Provided Non-IT staffing solutions across administrative and operational roles",
    "Trained and mentored students and job seekers with industry-relevant skills",
    "Helped candidates successfully secure placements in IT roles",
    "Maintained long-term relationships with clients through consistent delivery and support",
];

const impacts = [
    {
        text: "Faster hiring turnaround time",
        icon: <FaBolt />,
    },
    {
        text: "Improved candidate quality and retention",
        icon: <FaUserCheck />,
    },
    {
        text: "Strong client satisfaction and repeat business",
        icon: <FaHandshake />,
    },
    {
        text: "Career growth opportunities for students and professionals",
        icon: <FaChartLine />,
    },
];

/* ================= COUNTER HOOK ================= */

function useCountUp(target, duration = 2000) {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const started = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !started.current) {
                    started.current = true;
                    const startTime = performance.now();

                    const animate = (now) => {
                        const elapsed = now - startTime;
                        const progress = Math.min(elapsed / duration, 1);
                        // ease-out
                        const eased = 1 - Math.pow(1 - progress, 3);
                        setCount(Math.round(eased * target));
                        if (progress < 1) requestAnimationFrame(animate);
                    };

                    requestAnimationFrame(animate);
                }
            },
            { threshold: 0.4 }
        );

        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [target, duration]);

    return { count, ref };
}

/* ================= STAT ITEM ================= */

const StatItem = ({ value, suffix, label }) => {
    const { count, ref } = useCountUp(value);
    return (
        <motion.div
            className="col-6 col-md-3 about-stat-item"
            ref={ref}
            variants={scaleIn}
        >
            <span className="about-stat-number">{count}{suffix}</span>
            <span className="about-stat-label">{label}</span>
        </motion.div>
    );
};

/* ================= COMPONENT ================= */

const AboutUs = () => {
    return (
        <motion.div
            className="about-us-page-wrapper"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
        >
            <SEO
                title="About Us"
                description="Learn about Fortune Cloud Solutions - Your trusted partner in IT Staffing, Training, and Career Growth."
            />

            <Banner title="About Us" image="/images/about-banner.jpg" />

            <div className="container">
                <Breadcrumb />
            </div>

            {/* ===== ABOUT INTRO SECTION ===== */}
            <section className="about-intro-section">
                <div className="container">
                    <div className="row align-items-center gy-5">

                        {/* LEFT CONTENT */}
                        <motion.div
                            className="col-lg-6 about-intro-content"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-80px" }}
                        >
                            <div className="col-6 col-md-3">
                                <span className="testmonial-badge mb-3 w-100">
                                    About Us
                                </span>
                            </div>

                            <h2 className="fw-bold mb-3">
                                Shaping Futures with <br />
                                <span className="purple-text">Expert IT Solutions</span>
                            </h2>

                            <p className="about-text">
                                At <strong>Fortune Cloud Solutions</strong>, we specialize in connecting the right talent with the right
                                opportunity. With a strong global presence, we provide scalable staffing solutions and
                                industry-focused training to meet modern business demands.
                            </p>

                            <p className="about-text">
                                Our expertise spans across <strong>IT staffing, Non-IT staffing, and student training</strong>, making us a
                                trusted partner for both companies and job seekers.
                            </p>

                            <Link to="/contact">
                                <button className="discover-btn mt-4">
                                    Contact Us <FaArrowRight className="ms-2" />
                                </button>
                            </Link>
                        </motion.div>

                        {/* RIGHT IMAGE */}
                        <motion.div
                            className="col-lg-6"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-80px" }}
                        >
                            <div className="about-intro-img">
                                <img
                                    src={AboutImg}
                                    alt="Fortune Cloud Solutions Team Working"
                                    className="img-fluid"
                                />
                            </div>
                        </motion.div>

                    </div>
                </div>
            </section>

            {/* Our Global Presence */}
            <motion.section
                className="py-5 global-section position-relative overflow-hidden"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true, amount: 0.2 }}
            >
                {/* Decorative Background Elements */}
                <div className="global-bg-decoration">
                    <div className="blob blob-1"></div>
                    <div className="blob blob-2"></div>
                </div>

                <div className="container position-relative z-1">
                    <div className="row align-items-center mb-5">
                        <div className="col-lg-6 text-center text-lg-start">
                            <motion.div
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true }}
                            >
                                <span className="testmonial-badge mb-3">
                                    Global Impact
                                </span>
                                <h2 className="fw-bold mb-3 display-5">
                                    Our <span className="purple-text">Global Presence</span>
                                </h2>
                            </motion.div>
                        </div>
                        <div className="col-lg-6 text-center text-lg-start">
                            <motion.div
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true }}
                            >
                                <p className="text-muted fs-5 mb-4 max-w-500">
                                    We proudly serve clients and candidates across key international markets,
                                    bridging talent gaps with local expertise and global reach.
                                </p>
                            </motion.div>
                        </div>
                    </div>

                    <div className="row g-4 justify-content-center">
                        <motion.div
                            className="col-lg-5 d-none d-lg-block"
                            initial={{ x: -60, opacity: 0 }}
                            whileInView={{ x: 0, opacity: 1 }}
                            transition={{ duration: 1, ease: "easeOut" }}
                            viewport={{ once: true }}
                        >
                            <div className="global-presence-hero">
                                <img
                                    src={GlobalPresence}
                                    alt="Global Presence Map"
                                    className="img-fluid rounded-4 shadow-2xl"
                                />
                                <div className="floating-badge">
                                    <FaChartLine className="me-2" />
                                    <span>Expanding Globally</span>
                                </div>
                            </div>
                        </motion.div>

                        <div className="col-lg-7">
                            <motion.div
                                className="row g-4"
                                variants={staggerContainer}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true }}
                            >
                                {countries.map((item, index) => (
                                    <motion.div
                                        key={index}
                                        className="col-md-6"
                                        variants={fadeUp}
                                    >
                                        <motion.div
                                            className="global-country-card"
                                            whileHover={{ y: -10, boxShadow: "0 20px 40px rgba(157, 135, 189, 0.2)" }}
                                        >
                                            <div className="card-header-flex">
                                                <div className="country-icon">
                                                    {item.icon}
                                                </div>
                                                <div className="country-info">
                                                    <span className="region-tag">{item.region}</span>
                                                    <h4 className="mb-0 mt-1">{item.name}</h4>
                                                </div>
                                            </div>
                                            <p className="country-desc mt-3">{item.desc}</p>
                                            <div className="card-hover-indicator"></div>
                                        </motion.div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* Experience Section */}
            <motion.section
                className="py-5 experience-section position-relative"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true, amount: 0.2 }}
            >
                <div className="container">
                    {/* Heading Area */}
                    <div className="row justify-content-center mb-5">
                        <motion.div
                            className="col-lg-8 text-center"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true }}
                        >
                            <span className="testmonial-badge mb-3">
                                Proven Expertise
                            </span>
                            <h2 className="fw-bold mb-3 display-5">
                                Our <span className="purple-text">Experience</span>
                            </h2>
                            <p className="text-muted fs-5 mx-auto" style={{ maxWidth: "800px" }}>
                                At Fortune Cloud Solutions, we bring decades of combined experience in delivering 
                                high-quality staffing and training solutions across global markets. 
                                Our reliability, speed, and precision in talent delivery set us apart.
                            </p>
                        </motion.div>
                    </div>

                    {/* Highlights Grid */}
                    <motion.div 
                        className="row g-4"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true }}
                    >
                        {highlights.map((item, index) => (
                            <motion.div
                                key={index}
                                className="col-lg-4 col-md-6"
                                variants={fadeUp}
                            >
                                <motion.div 
                                    className="experience-detail-card"
                                    whileHover={{ 
                                        y: -8, 
                                        boxShadow: "0 15px 35px rgba(157, 135, 189, 0.15)",
                                        borderColor: "#9D87BD"
                                    }}
                                >
                                    <div className="experience-card-inner">
                                        <div className="check-icon-wrapper">
                                            <FaCheckCircle className="check-icon" />
                                        </div>
                                        <p className="mb-0 experience-highlight-text">{item}</p>
                                    </div>
                                    <div className="experience-card-accent"></div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </motion.section>

            {/* Impact Section */}
            <motion.section
                className="py-5 impact-section"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true, amount: 0.1 }}
            >
                <div className="container">

                    {/* Heading */}
                    <h2 className="fw-bold text-center mb-4 text-purple">
                        Our Impact
                    </h2>

                    {/* Cards */}
                    <div className="row justify-content-center">
                        {impacts.map((item, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3 mb-3"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1, duration: 0.5 }}
                                viewport={{ once: true }}
                            >
                                <div className="impact-card p-4 text-center h-100">
                                    <div className="icon-wrapper mb-3">
                                        {item.icon}
                                    </div>
                                    <p className="mb-0 text-dark fw-medium">{item.text}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                </div>
            </motion.section>

            {/* ===== FEATURES SECTION ===== */}
            {/* <section className="about-features-section">
                <div className="container">

                    <motion.div
                        className="text-center mb-5"
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: "-80px" }}
                    >
                        <span className="testmonial-badge mb-3">
                            <span className="testmonial-sparkle">✨</span>
                            What We Offer
                        </span>
                        <h2 className="fw-bold mt-3">
                            Your Trusted Partner in IT <br />
                            <span className="purple-text">Staffing, Training & Career Growth</span>
                        </h2>
                    </motion.div>

                    <motion.div
                        className="row g-4"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: "-80px" }}
                    >
                        {features.map((item, i) => (
                            <motion.div className="col-md-4" key={i} variants={fadeUp}>
                                <div className="about-feature-card">
                                    <div className="about-feature-icon">{item.icon}</div>
                                    <h3>{item.title}</h3>
                                    <p>{item.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>

                </div>
            </section> */}

            {/* ===== STATS SECTION ===== */}
            <section className="about-stats-section">
                <motion.div
                    className="container"
                    variants={staggerContainer}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-80px" }}
                >
                    <div className="row">
                        {stats.map((item, i) => (
                            <StatItem key={i} {...item} />
                        ))}
                    </div>
                </motion.div>
            </section>

            {/* ===== WHY CHOOSE US SECTION ===== */}
            <section className="about-why-section py-5">
                <div className="container">

                    <motion.div
                        className="text-center mb-5"
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: "-80px" }}
                    >
                        <span className="testmonial-badge mb-3">
                            Why Choose Us
                        </span>
                        <h2 className="fw-bold mt-3">
                            Why Businesses & Professionals <br />
                            <span className="purple-text">Trust Fortune Cloud Solutions</span>
                        </h2>
                    </motion.div>

                    <motion.div
                        className="row g-4 justify-content-center"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: "-80px" }}
                    >
                        {whyChooseUs.map((item, i) => (
                            <motion.div className="col-md-6 col-lg-4" key={i} variants={fadeUp}>
                                <div className="about-why-card bg-white">

                                    <div className="about-why-image">
                                        <img src={item.image} alt={item.title} />
                                    </div>

                                    <h3 className='text-uppercase'>{item.title}</h3>
                                    <p>{item.desc}</p>

                                </div>
                            </motion.div>
                        ))}
                    </motion.div>

                </div>
            </section>

            {/* ===== CTA SECTION ===== */}
            <section className="about-cta-section">
                <motion.div
                    className="container"
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-80px" }}
                >
                    <h2 className='text-uppercase'>Let Us Help Your Business Move Forward</h2>
                    <p>
                        Fortune Cloud Solutions provides tailored IT staffing, industry-relevant training,
                        and career development services, empowering businesses and professionals to thrive
                        in the ever-evolving IT landscape.
                    </p>
                    <Link to="/contact">
                        <button className="about-cta-btn text-uppercase">
                            Get In Touch <FaArrowRight className="ms-2" />
                        </button>
                    </Link>
                </motion.div>
            </section>



        </motion.div>
    );
};

export default AboutUs;
